import {
  createFileRoute,
  Outlet,
  useLoaderData,
  redirect,
} from "@tanstack/react-router";
import { SidebarMenu } from "~/components/Admin/SidebarMenu";
import { TopPanel } from "~/components/Admin/TopPanel";
import { SupportBadge } from "~/components/Layout/SupportBadge";
import { BookmarkHeartIcon } from "~/icons/BookmarkHeartIcon";
import { FormIcon } from "~/icons/FormIcon";
import { PasswordIcon } from "~/icons/PasswordIcon";
import { PinIcon } from "~/icons/PinIcon";
import { SendArrowDownIcon } from "~/icons/SendArrowDownIcon";
import { UserIcon } from "~/icons/UserIcon";
import { getFooter } from "~/lib/server-functions/get-footer";
import { Footer } from "~/routes/__main/route";
import { MenuItem } from "~/types/menu";
import { getUserSession } from "~/lib/api/auth.api";
import { useAdvertiserInquiryCount } from "~/lib/tanstack-query/hooks/useAdvertiserInquiryCount";
import { HeartPlusIcon } from "~/icons/HeartPlus";
import { authQueries } from "~/lib/queries";
import { useState } from "react";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";
import { withLocale } from "~/lib/utils/with-locale";

export const Route = createFileRoute("/advertiser/_main")({
  beforeLoad: async () => {
    const sessionData = await getUserSession();

    if (!sessionData?.user) {
      throw redirect({
        to: withLocale("/sign-in"),
      });
    }

    if (!sessionData.user.isAdvertiser) {
      throw redirect({
        to: withLocale("/sign-in"),
      });
    }
  },
  component: RouteComponent,
  loader: async ({ context }) => {
    // Prefetch auth session server-side so it's available immediately
    const userSession = await getUserSession();
    context.queryClient.setQueryData(authQueries.user().queryKey, userSession);

    return await getFooter();
  },
});

function RouteComponent() {
  const footer = useLoaderData({ from: "/advertiser/_main" });
  const { data: inquiryCount = 0 } = useAdvertiserInquiryCount();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const menuItems: MenuItem[] = [
    {
      label: "Benutzerkonto",
      icon: <UserIcon />,
      items: [
        {
          label: "Benutzerkonto bearbeiten",
          to: withLocale("/user"),
          icon: <FormIcon />,
        },
        {
          label: "Passwort ändern",
          to: withLocale("/password"),
          icon: <PasswordIcon />,
        },
        {
          label: "Rechnungsdaten",
          to: withLocale("/billing"),
          icon: <FormIcon />,
        },
      ],
    },
    {
      label: "Meine Locations",
      to: withLocale("/locations"),
      icon: <PinIcon />,
    },
    {
      label: "Meine Anfragen",
      to: withLocale("/inquiries"),
      icon: <HeartPlusIcon />,
      badge: inquiryCount,
    },
  ];
  return (
    <>
      <SupportBadge menuIsOpen={menuIsOpen} />
      <TopPanel menuIsOpen={menuIsOpen} onMenuToggle={setMenuIsOpen} />
      <div className="pt-16 layout-content-grid max-w-(--layout-page-max-width) mx-auto ring ring-gray-200 shadow-2xl min-h-[calc(100vh-var(--top-panel-height))] grid-rows-[1fr_auto]">
        <div className="flex gap-24">
          <div
            className={cn(
              "shadow-xl lg:shadow-none lg:translate-0 shrink-0 fixed top-(--top-panel-height) bg-white z-10 w-full lg:w-[300px] left-0 lg:static transition-transform",
              menuIsOpen ? "translate-y-0" : "-translate-y-[120%]",
            )}
          >
            <SidebarMenu items={menuItems} />
          </div>
          <Outlet />
        </div>
        <Footer
          supportText={footer?.data.supportText}
          phonenumber={footer?.data.phonenumber}
          email={footer?.data.email}
          brandMessage={footer?.data.brandMessage}
          logo={
            footer?.data.logoIdentifier
              ? buildImageUrl(footer.data.logoIdentifier, {
                  size: IMAGE_SIZES.logo,
                })
              : ""
          }
          linksBaseUrl={import.meta.env.VITE_BASE_URL}
          linksUpper={footer?.data.linksUpper || []}
          linksLower={footer?.data.linksLower || []}
        />
      </div>
    </>
  );
}
